import { Injectable, InjectionToken, Input } from '@angular/core';
import { ContentBlockType } from './content-block-type';
import { ContentBlock } from './content-block';
import { ApiArticle, ApiPage } from '@tytapp/api';

/**
 * Provides a way for content block implementations to be aware of the
 * current page. This is provided by the PageViewComponent. Consumer should
 * make sure to inject this optionally so that use of the block outside of the
 * page system (for instance within Articles) does not cause injection errors.
 */
export const CURRENT_PAGE_PROVIDER = new InjectionToken<{ readonly page: ApiPage }>('CURRENT_PAGE');
export const CURRENT_ARTICLE_PROVIDER = new InjectionToken<{ readonly article: ApiArticle }>('CURRENT_ARTICLE');

/**
 * Responsible for managing the available content block types, used in the Content Editor which is the core of both
 * Articles and Pages on TYT.com.
 */
@Injectable()
export class ContentBlocksService {
    private types: ContentBlockType[] = [];

    register(type: ContentBlockType) {
        if (this.types.some(x => x.id === type.id))
            return;

        if (type.preview)
            type.preview['type'] = type.id;

        this.types.push(type);
    }

    getPreview(type: ContentBlockType): ContentBlock {
        return type.preview as ContentBlock;
    }

    all() {
        return this.types.slice();
    }

    getFromBlock(block: ContentBlock) {
        return this.getById(this.getTypeFromBlock(block));
    }

    getTypeFromBlock(block: ContentBlock) {
        if (block.type)
            return block.type;

        let inferredType = this.inferTypeFromBlock(block);

        if (inferredType !== undefined) {
            block.type = inferredType;
        }

        return block.type
    }

    /**
     * Attempt to infer the type of a block from its contents, this may be needed for
     * old content in production.
     *
     * @param block
     * @returns
     */
    inferTypeFromBlock(block: ContentBlock & Record<string, any>) {
        if (block.image)
            return 'image';

        if (block.html_embed) {
            if (block.html_embed.startsWith('$offer:')) {
                return 'offer-set';
            } else if (block.html_embed.startsWith('$tweet:')) {
                return 'tweet';
            } else if (block.html_embed.startsWith('$youtube:')) {
                return 'youtube';
            }

            return 'embed';
        }

        if (block.body)
            return 'text';

        return undefined;
    }

    getById(id: string) {
        return this.types.find(x => x.id === id);
    }
}